export const CleanTextRegex = /[^\p{L}\p{N}\p{P}\p{S} ]/gu;
export const phoneNumberLooseRegex =
  /^(\s)*(\+?\d{1,4}?[\s.-]?)?((\(\d{1,5}\))|\d{1,5})[\s.-]?\d{1,5}([\s.-]?\d{1,5}){1,4}(\s)*$/;
//Below should be the same as the phoneNumberLooseRegex but also allow empty
//or whitespace-only values (spreadsheet exports often contain stray spaces
//or non-breaking spaces in otherwise "blank" cells). Assembled from named
//parts to keep the pattern readable and under Sonar's regex-complexity limit.
const PHONE_COUNTRY_CODE = String.raw`\+?\d{1,4}[\s.-]?`;
const PHONE_AREA_CODE = String.raw`\(\d{1,5}\)|\d{1,5}`;
const PHONE_DIGIT_GROUPS = String.raw`(?:[\s.-]?\d{1,5}){2,5}`;
export const OptionalPhoneNumberRegex = new RegExp(
  `^\\s*(?:(?:${PHONE_COUNTRY_CODE})?(?:${PHONE_AREA_CODE})${PHONE_DIGIT_GROUPS})?\\s*$`
);
//Below should be the same as the phoneNumberLooseRegex but also allow an optional extensions like x1234, ext. 1234, extension 1234, #1234, ;ext=1234, p1234, 内線1234
// This regex excludes invalid area codes like 555, 000, 111, etc.
export const LoosePhoneNumberWithExtensionRegex =
  /^(\s)*(\+?\d{1,4}?[\s.-]?)?((\(\d{1,5}\))|\d{1,5})[\s.-]?\d{1,5}([\s.-]?\d{1,5}){1,4}(\s)*((\s*,\s*\d{1,5})|(\s*(?:x|ext\.?|extension|内線)\s*\d{1,5})|(\s*#\s*\d{1,5})|(\s*p\s*\d{1,5})|(\s*;ext\s*=\s*\d{1,5})){0,4}(\s)*$|^(\s)*$/;

export const emailRegex =
  /^(?:[a-zA-Z0-9._\+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,63})?[ ]?$/;
export const EMAIL_REQUIRED_REGEX =
  /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,63}$/;
export const stateRegex = /^([A-Z]{2})?[ ]?$/i;
export const zipRegex = /^(\d{5}(-\d{4})?)?[ ]?$/;
export const nameLooseRegex = /^(?!\s*$)[\p{L}\p{N}\s.'&,/+™®-]+$/u;
export const IsoDateTimeRegex =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?Z$/;
// Labels are translated (`@[Prénom](first_name)`), so only the id is restricted.
const FIELD_REGEX = /@\[([^[\]]+)\]\(([\w\s]+)\)/g;

// Matches both `{token}` and the mustache `{{token}}` form, as Galactus does.
// Consuming the outer braces matters: a name-only match would render `{{x}}` as
// `{value}` and leak the value back out as a token name. Surrounding whitespace is
// trimmed by the callers instead of matched here — `\s*` beside `[^{}]+` makes the
// split ambiguous, which costs minutes on script text holding an unclosed brace.
const BRACE_TOKEN_REGEX = /\{\{?([^{}]+)\}?\}/g;

export const GetDynamicContentBE = (
  str: string | undefined,
  overrides?: { [key: string]: string }
): string => {
  if (str === undefined || !str) {
    return "";
  }

  let result = str;

  // Galactus resolves tokens case-insensitively, so a script written as
  // {First_Name} must render here the same way it will on the call.
  const lookup = new Map(
    Object.entries(overrides ?? {}).map(([key, value]) => [
      key.toLowerCase(),
      value,
    ])
  );

  const matches = result.match(FIELD_REGEX);

  if (matches) {
    matches.forEach((match) => {
      const variable = match.substring(
        match.indexOf("(") + 1,
        match.indexOf(")")
      );
      result = result.replace(
        match,
        lookup.get(variable.toLowerCase()) ?? `{${variable}}`
      );
    });
  }

  // Backend `{variable_id}` form (e.g. already saved wire format). UI `@[label](id)` is handled
  // above; when overrides are provided (e.g. try-call), substitute both styles.
  if (overrides) {
    result = result.replace(BRACE_TOKEN_REGEX, (full, variable: string) => {
      const key = variable.trim().toLowerCase();
      if (!key) {
        return full;
      }
      const value = lookup.get(key);
      if (value !== undefined) {
        return value;
      }
      // Legacy aliases aligned with GetDynamicContentUI / stored data
      if (key === "fname") {
        return lookup.get("first_name") ?? full;
      }
      if (key === "lname") {
        return lookup.get("last_name") ?? full;
      }
      if (key === "agencyname" || key === "agency_name") {
        return lookup.get("business_name") ?? full;
      }
      return full;
    });
  }

  return result.trim();
};

/** Resolved by realtime-agent at call time, so it is expected to survive substitution. */
const PASSTHROUGH_TOKENS = new Set(["time_of_day"]);

// Script text also contains prose and JSON examples; only bare identifiers are tokens.
const TOKEN_NAME_REGEX = /^\w+$/;

/**
 * Ordinal order, matching how Galactus sorts the same names onto its trace tags.
 * `localeCompare` would order by the caller's locale and split telemetry groups.
 */
export const CompareTokenNames = (a: string, b: string): number => {
  if (a < b) return -1;
  return a > b ? 1 : 0;
};

/**
 * Token names still present after {@link GetDynamicContentBE} substituted what
 * it could. These resolve to an empty string when the backend re-renders a
 * stored script, and are spoken literally when an inline script is used.
 */
export const GetUnresolvedTokens = (
  ...texts: (string | null | undefined)[]
): string[] => {
  const unresolved = new Set<string>();

  for (const text of texts) {
    if (!text) continue;
    for (const [, variable] of text.matchAll(BRACE_TOKEN_REGEX)) {
      // Lower-cased to match the backend, which groups tokens case-insensitively.
      const key = variable.trim().toLowerCase();
      if (TOKEN_NAME_REGEX.test(key) && !PASSTHROUGH_TOKENS.has(key)) {
        unresolved.add(key);
      }
    }
  }

  return [...unresolved].sort(CompareTokenNames);
};

export const GetDynamicContentUI = (
  str: string | undefined,
  variables: string[],
  t?: (val: string) => string
): string => {
  if (str === undefined || !str) {
    return "";
  } else if (variables.length === 0) {
    return str;
  }

  let result = str;

  result = result
    .replaceAll("{fname}", "{first_name}")
    .replaceAll("{lname}", "{last_name}")
    .replaceAll(
      "{agencyname}",
      variables.includes("business_name") ? "{business_name}" : "{agency_name}"
    )
    .replaceAll(
      "{agency_name}",
      variables.includes("business_name") ? "{business_name}" : "{agency_name}"
    );

  const regex = new RegExp(variables.map((v) => `{${v}}`).join("|"), "gi");
  const matches = result.match(regex);

  if (matches) {
    matches.forEach((match) => {
      let variable = match.replace("{", "").replace("}", "");
      result = result.replace(
        match,
        `@[${t ? t(variable) : variable}](${variable})`
      ); //"@[Agency Name](Agency Name)"
    });
  }

  return result;
};

export const UpdateDynamicContentUI = (
  str: string | undefined,
  ct: (str: string) => string
): string => {
  if (str === undefined || !str) {
    return "";
  }

  let result = str;

  // First find correctly formatted variables - @[display](variable_id)
  // This regex looks for display names followed by variable IDs that include underscores
  const validRegex = /@\[([\w\s]+)\]\(([\w\s]+)\)/g;
  const validMatches = result.match(validRegex);

  if (validMatches) {
    validMatches.forEach((match) => {
      const variablePart = match.substring(
        match.indexOf("(") + 1,
        match.indexOf(")")
      );

      // Update display text with proper translation, but keep the variable ID
      result = result.replace(match, `@[${ct(variablePart)}](${variablePart})`);
    });
  }

  // We do not fix or modify invalid variables, so they can be highlighted in red

  return result;
};
